import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '@app/environment/environment';
import { isInvestOpen } from '@app/helpers/project.helper';
import { MainImage, Project } from '@app/models/project.models';
import { PeriodDurationPipe } from '@app/pipes/period-duration.pipe';
import { ThreeDecimalsPipe } from '@app/pipes/three-decimals.pipe';
import { ProjectService } from '@app/services/projects/service';
import { AuthStore } from '@app/stores/auth.store';
import { ProjectsStore } from '@app/stores/projects/projects.store';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable, Observer, Subscription, switchMap } from 'rxjs';

export type ProjectDisplayType =
  | 'public'
  | 'myProjects'
  | 'admin'
  | 'acceptedInvestments'
  | 'pendingInvestments'
  | 'declinedInvestments'
  | 'investments'
  | 'followed'
  | 'followedUnderPreparation'
  | 'underPreparation'
  | 'closed'
  | 'myUnderPreparationProjects'
  | 'myUnderVerificationProjects'
  | 'myRunningProjects';

@Component({
  selector: 'app-project-vertical-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PeriodDurationPipe,
    ThreeDecimalsPipe,
  ],
  templateUrl: './project-vertical-card.component.html',
  styleUrl: './project-vertical-card.component.scss',
})
export class ProjectVerticalCardComponent {
  public project = input.required<Project>();
  public displayType = input.required<ProjectDisplayType>();

  public imageSrc: SafeUrl | null = null;

  public onDetailsClicked = output<string>();

  protected showDetailsButton = computed(() => {
    switch (this.displayType()) {
      case 'underPreparation':
      case 'followedUnderPreparation':
        return false;
      default:
        return true;
    }
  });

  protected showFollow = computed(() => {
    switch (this.displayType()) {
      case 'public':
      case 'myProjects':
      case 'myRunningProjects':
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'investments':
      case 'followed':
      case 'followedUnderPreparation':
        return this.authStore.isLoggedIn();
      case 'admin':
      case 'closed':
      case 'underPreparation':
      case 'myUnderPreparationProjects':
      case 'myUnderVerificationProjects':
        return false;
    }
    return false;
  });

  protected isInvestOpen = computed(() => {
    const p = this.project();
    return isInvestOpen(p.state, p.startCollectionDate, p.endCollectionDate);
  });
  protected imageLoading = signal(false);

  protected projectsStore = inject(ProjectsStore);

  private imageSubscription: Subscription | null = null;
  private imageRequestId = 0;

  private readonly authStore = inject(AuthStore);
  private readonly projectService = inject(ProjectService);
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  constructor() {
    effect((onCleanup) => {
      const images = this.getImages();
      this.imageSrc = null;
      this.imageSubscription?.unsubscribe();
      this.imageSubscription = null;

      if (images.length < 1) {
        this.imageLoading.set(false);
        return;
      }

      const currentRequestId = ++this.imageRequestId;
      const firstImage = images[0];
      this.imageLoading.set(true);

      const subscription = this.getImageUrl(firstImage.id).subscribe({
        next: (safeUrl) => {
          if (currentRequestId !== this.imageRequestId) {
            return;
          }
          this.imageSrc = safeUrl;
          this.imageLoading.set(false);
        },
        error: (error) => {
          if (currentRequestId !== this.imageRequestId) {
            return;
          }
          console.error('Error loading image:', error);
          this.imageSrc = null;
          this.imageLoading.set(false);
        },
      });

      this.imageSubscription = subscription;
      onCleanup(() => subscription.unsubscribe());
    });
  }

  getImages(): MainImage[] {
    return (
      this.project()?.mainImages?.filter((image) =>
        image.contentType?.includes('image'),
      ) ?? []
    );
  }

  getDateString(date: string) {
    return new Date(date).toLocaleDateString();
  }

  follow(projectId: string) {
    this.projectService.follow(projectId).subscribe({
      next: () => {
        this.projectsStore.addFollowedProject(this.project());
      },
    });
  }

  unFollow(projectId: string) {
    this.projectService.unFollow(projectId).subscribe({
      next: () => {
        this.projectsStore.removeFollowedProject(projectId);
      },
    });
  }

  private getImageUrl(imageId: string): Observable<SafeUrl | null> {
    const url = `${environment.apiUrl}/File/${imageId}`;

    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap((blob) => this.createDataUrlObservable(blob)),
      map((dataUrl) => this.sanitizer.bypassSecurityTrustUrl(dataUrl)),
    );
  }

  private createDataUrlObservable(blob: Blob): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
      reader.readAsDataURL(blob);

      return () => {};
    });
  }
}
